import React, { useState } from 'react';

import { CheckCircle, Circle } from 'lucide-react';

// framer animations
import { scaleMotionProps2 } from 'constants/animationProps';

//API queries
import {
  useCreateApplicationMutation,
  useGetUserInfoQuery,
} from 'redux_store/services/client/onboarding';

//libraries
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/browser';
import { AnimatePresence } from 'framer-motion';
import { motion } from 'framer-motion';
import fundmoreai from 'assets/fundmoreai.png';
import { useTranslation } from 'react-i18next';
import styles from '../../../styles/features/onboarding-layout.module.css';
/**
 * Page5 function handles the rendering and functionality of the fifth page of the onboarding process.
 *
 * @param {Object} props - The props object containing the necessary data and functions for the component.
 * @param {string} props.user - The user's name.
 * @param {function} props.setPage - The function to set the current page of the onboarding process.
 * @param {string} props.employment - The current employment status.
 * @param {function} props.setEmployment - The function to set the employment status.
 * @param {function} props.setIsOnboarding - The function to set the onboarding status.
 * @param {Object} props.dataForSubmission - The data for submission.
 * @param {function} props.saveOnboardingData - The function to save the onboarding data.
 * @param {boolean} props.isLoading - The loading status.
 * @param {function} props.setDirection - The function to set the direction.
 * @param {boolean} props.direction - The direction status.
 * @returns {JSX.Element} - The rendered component.
 */
const Page5 = ({
  user,
  setPage,
  employment,
  setEmployment,
  setIsOnboarding,
  dataForSubmission,
  saveOnboardingData,
  isLoading,
  setDirection,
  direction,
}) => {
  const { t } = useTranslation(['onboarding', 'common']);
  //router hook
  const navigate = useNavigate();
  //states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [employmentOption, setEmploymentOption] = useState('');

  //fetch user profiles
  const { data: profileData } = useGetUserInfoQuery(
    {},
    { refetchOnMountOrArgChange: true }
  );

  //for creating application id
  const [createApplication] = useCreateApplicationMutation();

  const resolvedEmploymentOption =
    employmentOption ||
    (employment === 'self'
      ? 'self_employed'
      : employment === 'retired'
        ? 'retired'
        : employment === 'employed'
          ? 'employed_ft'
          : '');

  const employmentOptions = [
    {
      id: 'employed_ft',
      label: t('onboarding:employmentType.employedFt'),
      value: 'employed',
    },
    {
      id: 'employed_pt',
      label: t('onboarding:employmentType.employedPt'),
      value: 'employed',
    },
    {
      id: 'self_employed',
      label: t('onboarding:employmentType.selfEmployed'),
      value: 'self',
    },
    {
      id: 'retired',
      label: t('onboarding:employmentType.retired'),
      value: 'retired',
    },
    {
      id: 'seasonal',
      label: t('onboarding:employmentType.seasonal'),
      value: 'employed',
    },
  ];

  //save onboarding data and create application id if user profile exists, else just save onboarding data
  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setDirection(true);
    try {
      const data = {
        lenderId: dataForSubmission.lenderId,
        loanPurposeId: dataForSubmission.loanPurpose === 'refinance' ? 2 : 1,
      };
      //check if user profile exists
      if (profileData?.data?.user_profiles?.length === 0) {
        const response = await createApplication({ data });
        if (response?.data?.status === 200) {
          setIsOnboarding(false);
          saveOnboardingData();
          //navigate to /profile
          navigate('/profile');
          setIsSubmitting(false);
        }
      } else if (profileData?.data?.user_profiles?.length > 0) {
        setIsOnboarding(false);
        saveOnboardingData();
        //navigate to /profile
        navigate('/profile');
        setIsSubmitting(false);
        //handle error
      }
    } catch (error) {
      Sentry.captureException(error);
      // Handle any errors that occurred during the submission process
      console.error('Error submitting application:', error);
    }
  };

  return (
    <div className={styles.contentWrapper}>
      <AnimatePresence mode="wait">
        <motion.div
          {...scaleMotionProps2}
          className={`${styles.formContainer} ${styles.fadeInContent}`}
        >
          <div className={styles.logoContainer}>
            <img src={fundmoreai} alt="Fundmore" className={styles.logoIcon} />
            <div className={styles.logoText}>
              <span className={styles.logoTextLight}>fund</span>
              <span className={styles.logoTextBold}>more</span>
              <span className={styles.logoTextGreen}>.ai</span>
            </div>
          </div>
          <span className={styles.srOnly}>
            {t('onboarding:greeting', { name: user })}
          </span>

          <div className={styles.progressDotsContainer}>
            <div className={`${styles.progressDot} ${styles.progressDotInactive}`} />
            <div className={`${styles.progressDot} ${styles.progressDotActive}`} />
          </div>

          <h2 className={styles.heading}>
            {t('onboarding:whatIsEmploymentType')}
          </h2>
          <p className={styles.subheading}>
            {t('onboarding:employmentSubtitle')}
          </p>

          <div className={styles.optionsContainerTight}>
            {employmentOptions.map((option) => (
              <div
                key={option.id}
                onClick={() => {
                  setEmployment(option.value);
                  setEmploymentOption(option.id);
                }}
                className={`${styles.optionCard} ${
                  resolvedEmploymentOption === option.id
                    ? styles.optionCardSelected
                    : styles.optionCardDefault
                }`}
              >
                <span className={styles.optionLabel}>
                  {option.label}
                </span>
                <div>
                  {resolvedEmploymentOption === option.id ? (
                    <div className={styles.choiceIconSelected}>
                      <CheckCircle size={20} className={styles.choiceIconSelectedMark} />
                    </div>
                  ) : (
                    <Circle size={24} className={styles.choiceIconUnselected} />
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className={`${styles.buttonContainer} ${styles.buttonGroup}`}>
            <button
              type="button"
              onClick={() => {
                setPage(4);
                setDirection(false);
              }}
              className={`${styles.button} ${styles.buttonSecondary} ${styles.buttonFlexOne}`}
            >
              {t('common:buttons.back')}
            </button>
            <button
              type="button"
              disabled={employment === '' && isSubmitting}
              onClick={handleSubmit}
              className={`${styles.button} ${styles.buttonPrimary} ${styles.buttonFlexTwo}`}
            >
              {isLoading && direction
                ? t('common:buttons.pleaseWait')
                : t('common:buttons.completeOnboarding')}
            </button>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default Page5;
